import * as queries from '../graphql/queries'
import * as mutations from '../graphql/mutations'
import { fetchAllPages } from '../utils/pagination'
import { getEntityConfig, getAllEntityConfigs } from './entityConfigLoader'
import { toPascalCase } from '../utils/stringUtils'
import type { EntityConfigDefinition, FieldLookupConfig } from '../types/entityConfig'

interface GraphQLClient {
  graphql: (params: { query: string; variables?: Record<string, any> }) => Promise<any>
}

interface CrudFunctions {
  loadFunction: (params?: Record<string, any>) => Promise<any>
  createFunction: (input: any) => Promise<any>
  updateFunction: (input: any) => Promise<any>
  deleteFunction: (input: any) => Promise<any>
}

interface EntityConfigWithCrud extends EntityConfigDefinition, CrudFunctions {}

export type { EntityConfigWithCrud };

// Export createCrudFunctions for use in components
export { createCrudFunctions };

// CRUD function generators
const createCrudFunctions = (
  entityName: string,
  client: GraphQLClient,
  fieldLookups: Record<string, FieldLookupConfig> = {}
): CrudFunctions => {
  const listName = `list${entityName}`
  const queryName = `list${toPascalCase(entityName)}`
  const mutationBase = toPascalCase(entityName)
  
  return {
    loadFunction: async (params: Record<string, any> = {}): Promise<any> => {
      const queriesAny = queries as any
      if (!queriesAny[queryName]) {
        return { data: { [listName]: { items: [] } } }
      }
      
      // Extract variables from params, defaulting to empty object
      const variables = params || {}
      
      const items = await fetchAllPages(client, queriesAny[queryName], variables, listName)
      
      // If no field lookups, return items as-is
      if (!fieldLookups || Object.keys(fieldLookups).length === 0) {
        return { data: { [listName]: { items } } }
      }
      
      // Load lookup data for all configured lookups (skip static lookups)
      const lookupPromises = Object.values(fieldLookups)
        .filter(lookup => lookup.lookupTable !== 'static')
        .map(lookup => {
          // Use direct table name mapping for queries
          const lookupQueryName = `list${toPascalCase(lookup.lookupTable)}`
          const lookupListName = `list${lookup.lookupTable}`
          
          if (!queriesAny[lookupQueryName]) {
            return Promise.resolve([])
          }
          return fetchAllPages(client, queriesAny[lookupQueryName], {}, lookupListName)
        })
      
      const lookupResults = await Promise.all(lookupPromises)
      const lookupData: Record<string, any> = {}
      
      // Map lookup results to their table names
      let resultIndex = 0
      Object.values(fieldLookups).forEach((lookup) => {
        if (lookup.lookupTable === 'static') {
          // Handle static lookups with predefined options
          lookupData[lookup.lookupTable] = lookup.options || {}
        } else {
          lookupData[lookup.lookupTable] = lookupResults[resultIndex]
          resultIndex++
        }
      })
      
      // Enhance items with lookup data
      const enhancedItems = items.map((item: any) => {
        const enhanced = { ...item }
        
        Object.entries(fieldLookups).forEach(([displayField, lookup]) => {
          if (lookup.lookupTable === 'static') {
            // Handle static lookups - displayField is the actual field name
            const staticOptions = lookup.options || {}
            const fieldValue = item[displayField]
            enhanced[displayField] = staticOptions[fieldValue] || fieldValue || ''
          } else {
            // Handle database lookups
            const lookupItems = lookupData[lookup.lookupTable]
            const matchedItem = lookupItems.find((lookupItem: any) => 
              lookupItem[lookup.foreignKey] === item[lookup.foreignKey]
            )
            
            if (matchedItem) {
              // Apply display format
              let displayValue = lookup.displayFormat || ''
              displayValue = displayValue.replace(`{${lookup.foreignKey}}`, matchedItem[lookup.foreignKey])
              displayValue = displayValue.replace(`{${lookup.displayField}}`, matchedItem[lookup.displayField])
              enhanced[displayField] = displayValue
            } else {
              enhanced[displayField] = ''
            }
          }
        })
        
        return enhanced
      })
      
      return { data: { [listName]: { items: enhancedItems } } }
    },
    createFunction: async (input: any): Promise<any> => {
      const mutationsAny = mutations as any
      const result = await client.graphql({ query: mutationsAny[`create${mutationBase}`], variables: { input } })
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message)
      }
      return result
    },
    updateFunction: async (input: any): Promise<any> => {
      const mutationsAny = mutations as any
      const result = await client.graphql({ query: mutationsAny[`update${mutationBase}`], variables: { input } })
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message)
      }
      return result
    },
    deleteFunction: async (input: any): Promise<any> => {
      const mutationsAny = mutations as any
      const result = await client.graphql({ query: mutationsAny[`delete${mutationBase}`], variables: { input } })
      if (result.errors && result.errors.length > 0) {
        throw new Error(result.errors[0].message)
      }
      return result
    }
  }
}

// Legacy entity configurations with CRUD functions
export const getEntityConfigs = (client: GraphQLClient): EntityConfigWithCrud[] => {
  // Get all entity configurations dynamically
  const allEntityConfigs = getAllEntityConfigs()
  
  // Custom business logic for specific entities
  const customLogic: Record<string, Partial<EntityConfigDefinition>> = {
    // REDIRECT_URL now uses generic system with field lookups
  }
  
  // Combine all entity configs with custom logic and CRUD functions
  const allConfigs: EntityConfigWithCrud[] = []
  
  Object.keys(allEntityConfigs).forEach(entityName => {
    const entityConfig = allEntityConfigs[entityName]
    const customConfig = customLogic[entityName] || {}
    
    // Merge entity config with custom logic and add CRUD functions
    const mergedConfig: EntityConfigWithCrud = {
      ...entityConfig,
      ...customConfig,
      ...createCrudFunctions(entityName, client, entityConfig.fieldLookups)
    } as EntityConfigWithCrud
    
    allConfigs.push(mergedConfig)
  })
  
  return allConfigs
}

// Export new configuration system
export { getEntityConfig, getAllEntityConfigs }
